import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LivestockTable from './LivestockTable';
import InfoIcon from '../common/InfoIcon';

// --- NEW: Pie Chart Label Rendering ---
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#888" fill="none" />
            <circle cx={ex} cy={ey} r={2} fill="#888" stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 6} y={ey} textAnchor={textAnchor} fill="#333">{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
        </g>
    );
};

// The legend component is now defined here for clarity
const StatusLegend = () => (
    <div className="text-left">
        <div className="flex items-center mb-1"><div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>Milking</div>
        <div className="flex items-center mb-1"><div className="w-3 h-3 rounded-full bg-purple-500 mr-2"></div>Pregnant</div>
        <div className="flex items-center mb-1"><div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>Dry (Not Milking)</div>
        <div className="flex items-center mb-1"><div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>Feeding</div>
        <div className="flex items-center mb-1"><div className="w-3 h-3 rounded-full bg-gray-400 mr-2"></div>Sold</div>
        <div className="flex items-center"><div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>Dead</div>
    </div>
);

const LivestockDashboard = ({
    livestock,
    isLoading,
    onSelectLivestock,
    onAddLivestockClick,
    onManageTypesClick,
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    livestockTypes,
    requestSort,
    sortConfig,
    filterStatus,
    setFilterStatus,
    onUpdateStatusClick,
}) => {
    const statusOptions = ["All", "Milking", "Dry (Not Milking)", "Pregnant", "Feeding", "Sold", "Dead","Active"];

    // --- NEW: Data processing for charts ---
    const statusChartData = useMemo(() => {
        const counts = livestock.reduce((acc, animal) => {
            const status = animal.status || "Unknown";
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [livestock]);

    const typeChartData = useMemo(() => {
        const counts = livestock.reduce((acc, animal) => {
            const type = animal.type || "Unknown";
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [livestock]);

    const STATUS_PIE_COLORS = {
        "Milking": "#3b82f6",
        "Pregnant": "#8b5cf6",
        "Dry (Not Milking)": "#f59e0b",
        "Feeding": "#22c55e",
        "Sold": "#6b7280",
        "Dead": "#ef4444",
    };
    
    const TYPE_PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF'];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-3">
                    <h2 className="text-2xl font-bold text-gray-800">Livestock Herd</h2>
                    <InfoIcon tooltip={<StatusLegend />} />
                </div>
                <div>
                    <button onClick={onManageTypesClick} className="inline-flex items-center px-4 py-2 mr-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Manage Types</button>
                    <button onClick={onAddLivestockClick} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">+ Add Animal</button>
                </div>
            </div>
            
            {/* --- MOVED: Filter section is now above the charts --- */}
            <div className="mb-8 p-4 bg-white rounded-lg shadow grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                    <label htmlFor="search" className="sr-only">Search</label>
                    <input type="text" id="search" placeholder="Search by name or tag..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="block w-full form-input rounded-md shadow-sm" />
                </div>
                <div>
                    <label htmlFor="filter-type" className="sr-only">Filter by Type</label>
                    <select id="filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="block w-full form-select rounded-md shadow-sm">
                        <option value="All">All Types</option>
                        {livestockTypes.map(type => (<option key={type.id} value={type.name}>{type.name}</option>))}
                    </select>
                </div>
                <div>
                    <label htmlFor="filter-status" className="sr-only">Filter by Status</label>
                    <select id="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="block w-full form-select rounded-md shadow-sm">
                        {statusOptions.map(status => (<option key={status} value={status}>{status === "All" ? "All Statuses" : status}</option>))}
                    </select>
                </div>
            </div>

            {/* --- Charts Section --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Herd Status Overview</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {statusChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={STATUS_PIE_COLORS[entry.name] || '#cccccc'} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} animal(s)`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Herd Type Overview</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 60, left: 60, bottom: 20 }}>
                            <Pie data={typeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {typeChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={TYPE_PIE_COLORS[index % TYPE_PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} animal(s)`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <LivestockTable 
                livestock={livestock} 
                isLoading={isLoading} 
                onSelectLivestock={onSelectLivestock} 
                onAddLivestockClick={onAddLivestockClick} 
                requestSort={requestSort}
                sortConfig={sortConfig}
                onUpdateStatusClick={onUpdateStatusClick}
            />
        </div>
    );
};

export default LivestockDashboard;
